import React, { Suspense } from "react";
import { useLocation } from "react-router-dom";
import { CirromLoadingLogo } from "./utils/CirromLoadingLogo";
import { LazyErrorBoundary } from "./utils/LazyErrorBoundary";
import { RouteErrorBoundary } from "./utils/RouteErrorBoundary";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";

interface LazyWrapperProps {
  children: React.ReactNode;
}

const loadingFallback = (
  <Box
    sx={{
      backgroundColor: "background.default",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      justifyContent: "center",
    }}
  >
    <Container maxWidth="sm" style={{ padding: 0 }}>
      <div
        style={{
          justifyContent: "center",
          display: "flex",
          margin: "10px",
        }}
      >
        <CirromLoadingLogo iconHeight="100px" />
      </div>
    </Container>
  </Box>
);

export const LazyWrapper: React.FC<LazyWrapperProps> = ({ children }) => {
  const location = useLocation();

  /*
   * Order matters. The chunk boundary is innermost so it gets first refusal on
   * a failed dynamic import and can still reload silently; a genuine render
   * error is rethrown past it and caught outside.
   *
   * Keyed on the path so navigating away from a broken route resets the
   * boundary - without it, the fallback would follow the user to every
   * subsequent page.
   */
  return (
    <RouteErrorBoundary key={location.pathname}>
      <LazyErrorBoundary fallback={loadingFallback}>
        <Suspense fallback={loadingFallback}>{children}</Suspense>
      </LazyErrorBoundary>
    </RouteErrorBoundary>
  );
};
